// Training analytics: overtraining detection heuristics from workout history.

const MUSCLE_GROUPS = {
  large: ['Quads', 'Hamstrings', 'Glutes', 'Back', 'Chest'],
  small: ['Shoulders', 'Arms', 'Core'],
};

const isLarge = (m) => MUSCLE_GROUPS.large.includes(m);

const daysAgo = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));

// Analyze last 7 days; return array of warning objects
export const detectOvertraining = (history, profile) => {
  const warnings = [];
  if (!history || history.length === 0) return warnings;

  const recent = history.filter((h) => daysAgo(h.date) < 7);
  if (recent.length === 0) return warnings;

  // 1. Heavy lifting density: >= 4 strength sessions in last 7 days
  if (recent.length >= 5) {
    warnings.push({
      level: 'moderate',
      title: 'High training density',
      message: `You've trained ${recent.length} times in the past 7 days. Consider a deload or skip a day.`,
    });
  }

  // 2. PR streaks: detect heavy load attempts via max weight per exercise
  // (Approximation: count sessions where any set's weight is in the top 10% of that exercise's lifetime history)
  const exerciseMaxes = {};
  history.forEach((h) => {
    (h.exercises || []).forEach((ex) => {
      (ex.sets || []).forEach((s) => {
        if (!s.completed) return;
        if (!exerciseMaxes[ex.refId] || s.weight > exerciseMaxes[ex.refId]) {
          exerciseMaxes[ex.refId] = s.weight;
        }
      });
    });
  });
  let heavyDaysCount = 0;
  recent.forEach((h) => {
    let heavyToday = false;
    (h.exercises || []).forEach((ex) => {
      const max = exerciseMaxes[ex.refId];
      if (!max) return;
      (ex.sets || []).forEach((s) => {
        if (s.completed && s.weight >= max * 0.9) heavyToday = true;
      });
    });
    if (heavyToday) heavyDaysCount++;
  });
  if (heavyDaysCount >= 4) {
    warnings.push({
      level: 'high',
      title: 'Heavy load streak',
      message: `${heavyDaysCount} of your last 7 sessions hit near-max weights. Drop to 70% for the next session to recover.`,
    });
  }

  // 3. Same large muscle group on consecutive days
  const recentSorted = [...recent].sort((a, b) => new Date(a.date) - new Date(b.date));
  for (let i = 1; i < recentSorted.length; i++) {
    const prev = recentSorted[i - 1];
    const cur = recentSorted[i];
    const dayDiff = daysAgo(prev.date) - daysAgo(cur.date);
    if (dayDiff !== 1) continue;
    const prevMuscles = new Set((prev.muscles || []).filter(isLarge));
    const curMuscles = (cur.muscles || []).filter(isLarge);
    const overlap = curMuscles.filter((m) => prevMuscles.has(m));
    if (overlap.length > 0) {
      warnings.push({
        level: 'moderate',
        title: `${overlap.join(', ')} hit two days in a row`,
        message: `Large muscle groups need 48+ hours to recover fully. Consider rotating to a different group today.`,
      });
      break; // one per scan is enough
    }
  }

  // 4. Full-body or upper-body work two days in a row (compound overlap)
  for (let i = 1; i < recentSorted.length; i++) {
    const prev = recentSorted[i - 1];
    const cur = recentSorted[i];
    const dayDiff = daysAgo(prev.date) - daysAgo(cur.date);
    if (dayDiff !== 1) continue;
    const prevTitle = (prev.title || '').toLowerCase();
    const curTitle = (cur.title || '').toLowerCase();
    if ((prevTitle.includes('full') && curTitle.includes('full')) ||
        (prevTitle.includes('upper') && curTitle.includes('upper')) ||
        (prevTitle.includes('lower') && curTitle.includes('lower'))) {
      warnings.push({
        level: 'moderate',
        title: 'Overlapping splits',
        message: 'Same split two days in a row stacks fatigue. Alternate or take a rest day.',
      });
      break;
    }
  }

  // Dedupe by title
  const seen = new Set();
  return warnings.filter((w) => {
    if (seen.has(w.title)) return false;
    seen.add(w.title);
    return true;
  });
};

// Pain banding (CI-9). Standard users: 0-3 clear · 4-6 lighter · 7-10 recover.
// Adaptive Mode users get a CONSERVATIVE shift — the mid band is the smallest
// because their injury propensity is elevated even on good days: 0-2 clear ·
// 3-5 lighter · 6-10 recover. `painBands` is the single source of these cut
// points so the editor, badge, cap, and auto-reg never disagree (mirrors the
// backend `_pain_bands` in pacing.py).
export const ADAPTIVE_CAPACITY_CEILING = 74; // Adaptive never reads "High / push"
export const painBands = (conservative = false) => (
  conservative ? { amberMin: 3, redMin: 6 } : { amberMin: 4, redMin: 7 }
);

// Pain-based workout adjustment
export const painAdjustment = (level, conservative = false) => {
  const n = Number(level);
  if (isNaN(n) || n < 0) return null;
  const { amberMin, redMin } = painBands(conservative);
  if (n < amberMin) return { tier: 'green', message: 'Cleared to train normally.', recommend: 'proceed' };
  if (n < redMin) return {
    tier: 'amber',
    message: 'Train lighter today — drop weights to 60–70% and skip max-effort sets. Listen to your body.',
    recommend: 'modify',
  };
  return {
    tier: 'red',
    message: 'We strongly recommend a rest day. Pushing through pain at this level risks injury setback. Active recovery only.',
    recommend: 'rest',
  };
};

// Should we show the pain check? Once per day for users with injuries.
export const shouldShowPainCheck = (profile, painLog) => {
  const hasInjuries = Object.values(profile.injuries || {}).some(Boolean);
  if (!hasInjuries) return false;
  const today = new Date().toISOString().slice(0, 10);
  return !painLog?.[today];
};


// ----- Daily Readiness (CI-5) -----
// SINGLE SOURCE OF TRUTH lives in the backend: compute_capacity() in
// backend/services/pacing.py, surfaced via GET /api/pacing → capacity. The
// Strength badge and the Dashboard PacingBanner both render THAT score, so the
// two surfaces can never disagree (the old 0-10 computeIntensity + the 0-100
// capacity scorer used to drift — that's the bug CI-5 closes).
//
// Two helpers below:
//   readinessFromCapacity(score) — map a 0-100 capacity score to the badge's
//     label/color/message. Bands MUST mirror compute_capacity's reason bands.
//   localCapacityFallback(pain, checkin) — recompute the score client-side for
//     the no-wearable case (web has no synchronous HealthKit), used ONLY while
//     /pacing is loading or the device is offline. Backend stays authoritative.
//
// Bands: ≥75 High · ≥55 Steady · ≥35 Lighter · <35 Recover · null → Check in

export const readinessFromCapacity = (score) => {
  if (score === null || score === undefined || isNaN(Number(score))) {
    return {
      score: null,
      label: 'Check in',
      color: 'text-enf-text-muted',
      message: 'Log a daily check-in (and pain, if any) on your Progress page to personalize today\u2019s readiness.',
    };
  }
  const s = Math.round(Number(score));
  if (s >= 75) {
    return {
      score: s, label: 'High', color: 'text-enf-accent-fg',
      message: 'Your body\u2019s reading strong today \u2014 green light to push.',
    };
  }
  if (s >= 55) {
    return {
      score: s, label: 'Steady', color: 'text-enf-role-info',
      message: 'You\u2019re in a steady zone \u2014 a normal session is well within range.',
    };
  }
  if (s >= 35) {
    return {
      score: s, label: 'Lighter', color: 'text-enf-role-warning',
      message: 'A lighter option today \u2014 not a smaller you. Keep the pattern.',
    };
  }
  return {
    score: s, label: 'Recover', color: 'text-enf-role-info',
    message: 'Your body is asking for less right now. Move with what it\u2019s giving you.',
  };
};

export const localCapacityFallback = (painLevel, wellbeingEntry, conservative = false) => {
  // Subjective 0-100 from the daily check-in — mirrors _subjective_capacity():
  // average the available 1-5 dims, map 1→0 .. 5→100.
  let subjective = null;
  if (wellbeingEntry && typeof wellbeingEntry === 'object') {
    const vals = ['energy', 'sleep', 'mood', 'function']
      .map((k) => Number(wellbeingEntry[k]))
      .filter((v) => !isNaN(v) && v > 0);
    if (vals.length) {
      const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      subjective = Math.max(0, Math.min(100, (avg - 1) * 25));
    }
  }
  // Pain cap — mirrors _pain_capacity_cap(). Bands come from painBands() so the
  // conservative (Adaptive) shift stays in lockstep with the editor + auto-reg.
  let cap = null;
  if (painLevel !== null && painLevel !== undefined && !isNaN(Number(painLevel))) {
    const n = Number(painLevel);
    const { amberMin, redMin } = painBands(conservative);
    if (n >= redMin) cap = 25;
    else if (n >= amberMin) cap = 50;
  }
  if (subjective === null && cap === null) return null; // cold-start
  let score = subjective === null ? Math.min(65, cap) : subjective;
  if (cap !== null && cap < score) score = cap;
  // Conservative ceiling: Adaptive users never read "High" — cap at top of Steady.
  if (conservative) score = Math.min(score, ADAPTIVE_CAPACITY_CEILING);
  return Math.round(score);
};

// ----- Daily closed-loop auto-regulation (CI-9) -----
// Combines today's readiness (0-100 capacity) and today's pain into ONE
// conservative, REDUCE-ONLY signal that scales the day's training volume. This
// is a training adjustment from self-report — NOT a medical interpretation —
// and it NEVER increases load or volume.
//   green → full session
//   amber → ease back  (trim top accessory tier + 1 set off accessory tiers)
//   red   → recover    (Tier 1 compounds only, sets capped low)
// Worse-of-the-two governs, so a good capacity read can't out-vote real pain
// (mirrors the pain cap in backend compute_capacity).
export const autoRegulationLevel = (capacityScore, painLevel, conservative = false) => {
  const rank = { green: 0, amber: 1, red: 2 };

  let painTier = 'green';
  const p = Number(painLevel);
  if (!isNaN(p) && p >= 0) {
    // Bands from painBands(): standard 4/7, conservative (Adaptive) 3/6. The mid
    // band is the smallest on purpose — pain at the red floor is protective-red.
    const { amberMin, redMin } = painBands(conservative);
    if (p >= redMin) painTier = 'red';
    else if (p >= amberMin) painTier = 'amber';
  }

  let capTier = 'green';
  if (capacityScore !== null && capacityScore !== undefined && !isNaN(Number(capacityScore))) {
    const c = Number(capacityScore);
    if (c < 35) capTier = 'red';
    else if (c < 55) capTier = 'amber';
  }

  const level = rank[painTier] >= rank[capTier] ? painTier : capTier;
  if (level === 'green') return { level: 'green', reason: null, driver: null };

  const driver = rank[painTier] > rank[capTier] ? 'pain'
    : rank[capTier] > rank[painTier] ? 'readiness'
    : 'both';
  const reason = driver === 'pain' ? 'pain is elevated today'
    : driver === 'readiness' ? 'your readiness is low today'
    : 'pain is elevated and readiness is low today';
  return { level, reason, driver };
};


// ---------- CI-9 Phase A — Regional auto-regulation (amputee) ----------
//
// Two functions:
//   regionalRegulationLevel() — converts today's residual-limb signals into a
//     green/amber/red level, same shape as autoRegulationLevel().
//   compoundRegLevel() — for exercises that ARE in the regional scope, takes
//     the worse-of systemic + regional and escalates amber+amber → red so that
//     a bad general day AND a bad limb day compounds on the affected movements.

/**
 * Converts residual-limb check-in signals into a regional regulation level.
 *
 * Inputs (all optional — missing = green for that signal):
 *   phantomPain   — numeric 0-10 from today's residual-limb log
 *   socketFit     — string id from SOCKET_FIT_OPTIONS (e.g. 'pain-point')
 *   skinStatus    — string id from SKIN_STATUS_OPTIONS (e.g. 'breakdown')
 *
 * Bands:
 *   Phantom ≥ 7 → red   (high phantom pain, significant load risk)
 *   Phantom ≥ 4 → amber (moderate phantom pain)
 *   Socket 'pain-point' → amber minimum
 *   Skin 'breakdown' or 'blistering' → amber minimum
 *   Worse-of-three governs.
 *
 * Framing note: this is a training nudge, not a medical instruction. The banner
 * copy must never say "don't load broken skin" — always "eased your
 * prosthesis-loaded work based on your residual-limb check-in."
 */
export const regionalRegulationLevel = (phantomPain, socketFit, skinStatus) => {
  const rank = { green: 0, amber: 1, red: 2 };

  let phantomTier = 'green';
  const p = Number(phantomPain);
  if (!isNaN(p) && p >= 0) {
    if (p >= 7) phantomTier = 'red';
    else if (p >= 4) phantomTier = 'amber';
  }

  const socketTier = socketFit === 'pain-point' ? 'amber' : 'green';

  const skinTier = (skinStatus === 'breakdown' || skinStatus === 'blistering') ? 'amber' : 'green';

  // Worst-of-three
  const worstRank = Math.max(rank[phantomTier], rank[socketTier], rank[skinTier]);
  const level = worstRank === 2 ? 'red' : worstRank === 1 ? 'amber' : 'green';

  if (level === 'green') return { level: 'green', reason: null, driver: null };

  const drivers = [];
  if (rank[phantomTier] === worstRank) drivers.push('phantom pain');
  if (rank[socketTier] === worstRank) drivers.push('socket fit');
  if (rank[skinTier] === worstRank) drivers.push('skin condition');
  const driverStr = drivers.join(' and ');

  return {
    level,
    reason: `${driverStr} flagged in your residual-limb check-in`,
    driver: drivers.length === 1 ? drivers[0] : 'multiple',
  };
};

/**
 * Compounds the systemic (whole-body) and regional (limb-adjacent) regulation
 * levels for exercises that are in the regional scope.
 *
 * The compound rule: amber + amber escalates to red for affected movements.
 * A moderate bad day everywhere + moderate phantom pain = protect the limb
 * more carefully. The math is simple: rank sum ≥ 2 → red; ≥ 1 → amber.
 *
 * This only applies to exercises tagged as regionally affected. Unaffected
 * exercises see the systemic level only — no change.
 *
 * @param {'green'|'amber'|'red'} systemicLevel — from regLevel in Strength
 * @param {'green'|'amber'|'red'} regionalLevel — from regionalRegulationLevel()
 * @returns {'green'|'amber'|'red'}
 */
export const compoundRegLevel = (systemicLevel, regionalLevel) => {
  const rank = { green: 0, amber: 1, red: 2 };
  const combined = rank[systemicLevel] + rank[regionalLevel];
  if (combined >= 2) return 'red';
  if (combined >= 1) return 'amber';
  return 'green';
};


// ---------- PR counting (history-wide, cheap re-compute) ----------
//
// Mirrors detectPRs() in Strength.jsx but scans ALL of history so the
// Dashboard's "PRs Hit" tile has a real value. Same e1RM math (Epley) and
// the same "prior session required + ≥1 e1RM lb improvement" guard, so a
// movement's first-ever entry can't count as a PR.
//
// Returns the count of (exercise, workout) pairs where the workout's top
// completed set beat that exercise's prior best e1RM by ≥1 lb. A single
// workout can produce multiple PRs across different exercises.

const epley1RM = (weight, reps) => {
  const w = Number(weight) || 0;
  const r = Number(reps) || 0;
  if (w <= 0 || r <= 0) return 0;
  return w * (1 + r / 30);
};

const bestE1RMInExercise = (ex) => {
  let best = 0;
  for (const s of ex.sets || []) {
    if (!s.completed) continue;
    const e = epley1RM(s.weight, s.reps);
    if (e > best) best = e;
  }
  return best;
};

/**
 * Count PRs hit within the last `days` calendar days.
 * `history` is treated as canonical lb (weight) — works regardless of the
 * user's display units preference.
 */
export const countPRsInWindow = (history, days = 30) => {
  if (!Array.isArray(history) || history.length === 0) return 0;
  // Sort chronologically (oldest first) so we can compare each workout against
  // the running best from prior workouts only.
  const sorted = history.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
  const bestByRef = new Map(); // refId → highest e1RM seen in PRIOR workouts
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  let prCount = 0;

  for (const h of sorted) {
    const ts = new Date(h.date).getTime();
    const inWindow = ts >= cutoff;
    for (const ex of (h.exercises || [])) {
      if (!ex.refId) continue;
      const today = bestE1RMInExercise(ex);
      if (today <= 0) continue;
      const prev = bestByRef.get(ex.refId) || 0;
      if (inWindow && prev > 0 && today > prev + 1) {
        prCount += 1;
      }
      // Update running best AFTER PR check so each workout compares against
      // strict prior history.
      if (today > prev) bestByRef.set(ex.refId, today);
    }
  }
  return prCount;
};
